import { useMemo, useState } from 'react'
import { Gamepad2, Monitor, Phone, Volume2 } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { useSocialStore, displayName } from '@/store/social'
import { usePresenceStore } from '@/store/presence'
import { useGuildStore } from '@/store/guilds'
import { friendActivity, type FriendActivityGroup } from '@/lib/friendActivity'
import { avatarUrl } from '@/lib/avatar'
import { BASE } from '@/lib/api'
import { describeError } from '@/lib/auth'
import { openChannelLink } from '@/lib/navigate'
import { plural } from '@/lib/guildUi'
import { Avatar } from '../Avatar'
import { StyledName } from '../StyledName'
import { cx } from '@/lib/cx'
import styles from './FriendActivity.module.css'

export function FriendActivity() {
  const selfId = useAuthStore(s => s.user?.id)
  const relationships = useSocialStore(s => s.relationships)
  const presences = useSocialStore(s => s.presences)
  const activities = usePresenceStore(s => s.activities)
  const privateChannels = useSocialStore(s => s.channels)
  const calls = useSocialStore(s => s.calls)
  const callStates = useSocialStore(s => s.callStates)
  const guilds = useGuildStore(s => s.guilds)
  const channels = useGuildStore(s => s.channels)
  const members = useGuildStore(s => s.members)
  const voiceStates = useGuildStore(s => s.voiceStates)
  const groups = useMemo(() => friendActivity({ selfId, relationships, presences, activities, privateChannels, calls, callStates, guilds, channels, members, voiceStates }), [selfId, relationships, presences, activities, privateChannels, calls, callStates, guilds, channels, members, voiceStates])
  return <aside className={cx(styles.panel, 'scroller')} aria-label="Активность друзей">
    <h2 className={styles.heading}>Активность друзей</h2>
    {groups.length ? groups.map(group => <ActivityCard key={group.id} group={group} />) : <div className={styles.empty}><Gamepad2 size={28} aria-hidden="true" /><h3>Пока нет активности</h3></div>}
  </aside>
}

function ActivityCard({ group }: { group: FriendActivityGroup }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const openChat = async (id: string) => {
    const accountId = useAuthStore.getState().user?.id
    if (busy || !accountId) return
    setBusy(id); setError(null)
    try {
      const channelId = await useSocialStore.getState().openDM(id)
      if (useAuthStore.getState().user?.id === accountId) useAppStore.getState().setView({ kind: 'home', channelId })
    }
    catch (err) { setError(describeError(err).message) }
    finally { setBusy(null) }
  }
  const Icon = group.kind === 'voice' ? Volume2 : group.kind === 'call' ? Phone : Gamepad2
  return <section className={styles.card} aria-label={group.title}>
    <div className={styles.summary}>
      {group.steam ? <GameCover key={group.steam} steam={group.steam} /> : <span className={styles.icon}><Icon size={22} aria-hidden="true" /></span>}
      <div className={styles.text}><h3 data-tooltip={group.title}>{group.title}</h3><p>{group.subtitle ?? (group.kind === 'game' ? 'Сейчас в игре' : 'В звонке')} · {group.friends.length} {plural(group.friends.length, 'друг', 'друга', 'друзей')}</p></div>
    </div>
    <div className={styles.friends}>{group.friends.map(friend => <button type="button" key={friend.user.id} className={styles.friend} disabled={Boolean(busy)} aria-label={`Написать ${displayName(friend.user)}`} data-tooltip={`Написать ${displayName(friend.user)}`} onClick={() => void openChat(friend.user.id)}>
      <Avatar size={28} initial={displayName(friend.user).slice(0, 1)} color={`hsl(${Number(friend.user.id.slice(-4)) % 360} 55% 50%)`} status={friend.status} src={avatarUrl(friend.user.id, friend.user.avatar)} decoration={friend.user.avatar_decoration_data} />
      <span className={styles.name}><StyledName name={displayName(friend.user)} style={friend.user.display_name_styles} /></span>
      {friend.streaming ? <Monitor size={16} className={styles.streaming} aria-label="Транслирует" data-tooltip="Транслирует" /> : null}
    </button>)}</div>
    {group.channelId ? <button type="button" className={styles.open} onClick={() => void openChannelLink({ guildId: group.guildId ?? null, channelId: group.channelId!, messageId: null })}>{group.kind === 'voice' ? 'Открыть канал' : 'Открыть беседу'}</button> : null}
    {error ? <p role="alert" className={styles.error}>{error}</p> : null}
  </section>
}

function GameCover({ steam }: { steam: string }) {
  const [broken, setBroken] = useState(false)
  return broken ? <span className={styles.icon}><Gamepad2 size={22} aria-hidden="true" /></span> : <img className={styles.cover} src={`${BASE}/games/cover?steam=${encodeURIComponent(steam)}`} alt="" loading="lazy" onError={() => setBroken(true)} />
}
